import {Chip} from '@dropbox/dig-components/chip';
import {Menu} from '@dropbox/dig-components/menu';
import {Truncate} from '@dropbox/dig-components/truncate';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronRightLine, LinkLine} from '@dropbox/dig-icons/assets';
import {LinkIcon, linkToIcon} from 'components/shared/LinkIcon';
import {t} from 'i18next';
import {MouseEvent} from 'react';
import {
  dashboardLinkDestination,
  logDashboardInteraction,
} from 'views/home/dashboard/dashboardAnalytics';
import {useDashboardTileId} from 'views/home/dashboard/DashboardTileContext';
import {AgendaDescriptionLink} from 'views/home/dashboard/tiles/agendaDescriptionLinks';

/** Keep the menu above dashboard chrome (sticky search is z-index 4). */
const AGENDA_LINKS_MENU_Z_INDEX = 20;
/** Cap the menu (and each label) so long links can't balloon the popover. */
const MENU_LABEL_MAX_WIDTH = 260;

/**
 * Collapses an event's multiple description links into a single chip
 * ("N attached links") that opens a menu, instead of a long row of chips.
 */
export const AgendaLinksMenuChip = ({
  links,
  isLive,
}: {
  links: AgendaDescriptionLink[];
  isLive: boolean;
}) => {
  const tileId = useDashboardTileId();
  const label = t('dashboard_agenda_links_count', {count: links.length}).toString();

  return (
    <Menu.Wrapper
      shouldPropagateClickOutsideMouseEvents
      onToggle={({isOpen}) => {
        // Only log the open; onToggle also fires on close.
        if (isOpen && tileId) {
          logDashboardInteraction({
            action: 'agenda_links_menu_open',
            tileId,
            value: links.length.toString(),
          });
        }
      }}
    >
      {({getContentProps, getTriggerProps}) => {
        const triggerProps = getTriggerProps();
        return (
          <>
            <Chip
              {...triggerProps}
              type="button"
              size="small"
              variant={isLive ? 'transparent' : 'standard'}
              aria-label={label}
              className={
                isLive
                  ? atoms({
                      borderRadius: 'Large',
                      backgroundColor: {
                        default: 'Primary Surface - State 1',
                        hover: 'Primary Surface - State 2',
                      },
                    })
                  : undefined
              }
              onClick={(e: MouseEvent<HTMLElement>) => {
                // Don't toggle the expandable row underneath.
                e.stopPropagation();
                triggerProps.onClick?.(e);
              }}
              onMouseDown={(e: MouseEvent) => e.stopPropagation()}
            >
              <Chip.IconAccessory>
                <UIIcon src={LinkLine} size="small" className={atoms({flexShrink: 0})} />
              </Chip.IconAccessory>
              <Chip.Content>{label}</Chip.Content>
              <Chip.IconAccessory>
                <UIIcon
                  src={ChevronRightLine}
                  size="small"
                  className={atoms({color: 'Text Subtle'})}
                />
              </Chip.IconAccessory>
            </Chip>
            <Menu.Content
              {...getContentProps()}
              placement="bottom-start"
              style={{zIndex: AGENDA_LINKS_MENU_Z_INDEX}}
            >
              <Menu.Segment>
                {links.map((link) => (
                  <Menu.LinkItem
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    withLeftAccessory={<LinkIcon icon={linkToIcon(link.url)} size="small" />}
                    onClick={(e: MouseEvent) => {
                      e.stopPropagation();
                      if (tileId) {
                        logDashboardInteraction({
                          action: 'link_click',
                          tileId,
                          target: link.label,
                          destination: dashboardLinkDestination(link.url),
                        });
                      }
                    }}
                  >
                    <Box style={{maxWidth: MENU_LABEL_MAX_WIDTH}}>
                      <Truncate>{link.label}</Truncate>
                    </Box>
                  </Menu.LinkItem>
                ))}
              </Menu.Segment>
            </Menu.Content>
          </>
        );
      }}
    </Menu.Wrapper>
  );
};
